import { useMemo } from "react";

import {
  MaritimeArea,
  useMaritimeAreasQuery,
} from "@/features/dashboard/components/warnings/maritime-areas.queries";
import {
  MaritimeWarning,
  selectMaritimeWarnings,
  useMaritimeWarningsQuery,
} from "@/features/dashboard/components/warnings/maritime-warnings.queries";
import {
  getAreaName,
  getWarningDescription,
} from "@/features/dashboard/components/warnings/maritime-warnings.utils";
import {
  MARITIME_FORECAST_DOMAINS,
  MaritimeForecastDomainId,
} from "@/features/dashboard/components/warnings/maritime-forecast.domains";
import {
  MARITIME_FORECAST_PERIOD_KEYS,
  MARITIME_FORECAST_SHORT_RANGE_PERIOD_KEYS,
} from "@/features/dashboard/components/warnings/maritime-forecast-period-keys";
import { buildAreaPhenomenaText } from "@/features/dashboard/components/warnings/maritime-broadcast-phenomena.utils";
import { useMaritimeForecastQuery } from "@/features/dashboard/components/warnings/maritime-forecast.queries";
import {
  MaritimeBroadcastType,
  MaritimeBroadcastWidgetConfiguration,
  MaritimeForecast,
} from "@/lib/api";
import { isMaritimeBroadcastType } from "@/lib/api/maritime-widget-configuration";

import MaritimeBroadcastRenderer, {
  AreaLine,
  BroadcastPeriod,
  MaritimeBroadcastRendererProps,
  WarningGroup,
} from "./MaritimeBroadcastRenderer";
import {
  AMS,
  footerEN,
  footerNL,
  issuedEN,
  issuedNL,
  periodHeaderEN_noDay,
  periodHeaderNavtex,
  periodHeaderNL,
  periodHeaderSchip,
} from "./maritime-broadcast-publication-format.utils";

interface MaritimeBroadcastProps {
  id: string;
  data: unknown;
  configuration: MaritimeBroadcastWidgetConfiguration;
}

// ─── Product configuration ─────────────────────────────────────────────────────

/** i18n keys for broadcast phenomenon inline templates: `widgets.maritime_broadcast.phenomena.<id>` */
const MARITIME_BROADCAST_PHENOMENON_INLINE_KEYS = {
  wind: "widgets.maritime_broadcast.phenomena.wind",
  gust: "widgets.maritime_broadcast.phenomena.gust",
  weather: "widgets.maritime_broadcast.phenomena.weather",
  visibility: "widgets.maritime_broadcast.phenomena.visibility",
  significant_wave_height: "widgets.maritime_broadcast.phenomena.significant_wave_height",
} as const;

type MaritimeBroadcastPhenomenonInlineKeyRegistry = Record<string, string>;

type ProductConfig = {
  domain: MaritimeForecastDomainId;
  locale: "nl" | "en";
  canonicalAreaIds: string[];
  canonicalPhenomena: string[];
  phenomenaInlineKeys: MaritimeBroadcastPhenomenonInlineKeyRegistry;
  subtitle?: string;
  warningHeader: string;
  otherAreasHeader: string;
  otherAreasText: string;
  noWarningsText: string;
  synopsisHeader: string;
  allDistrictsText: string;
  littleChangeText: string;
  phenomenaSep: string;
  formatIssuedLine: (publishedAt: string) => string;
  formatPeriodHeader: (start: string, end: string) => string;
  formatFooter: (nextAt: string) => string[];
};

const PRODUCT_CONFIGS: Record<MaritimeBroadcastType, ProductConfig> = {
  marifoon_nl: {
    domain: MARITIME_FORECAST_DOMAINS.coastal,
    locale: "nl",
    canonicalAreaIds: [
      "flushing",
      "zierikzee",
      "hook-of-holland",
      "ijmuiden",
      "texel",
      "harlingen",
      "rottum",
      "delfzijl",
      "ijsselmeer",
      "marken",
    ],
    canonicalPhenomena: ["wind", "gust", "weather", "visibility"],
    phenomenaInlineKeys: MARITIME_BROADCAST_PHENOMENON_INLINE_KEYS,
    warningHeader: "Waarschuwing voor de scheepvaart:",
    otherAreasHeader: "Overige districten:",
    otherAreasText: "geen waarschuwing",
    noWarningsText: "Er zijn geen waarschuwingen van kracht",
    synopsisHeader: "Weeroverzicht:",
    allDistrictsText: "Alle districten",
    littleChangeText: "weinig verandering",
    phenomenaSep: ", ",
    formatIssuedLine: issuedNL(AMS, false),
    formatPeriodHeader: periodHeaderNL,
    formatFooter: footerNL(AMS, false),
  },
  marifoon_en: {
    domain: MARITIME_FORECAST_DOMAINS.coastal,
    locale: "en",
    canonicalAreaIds: [
      "flushing",
      "zierikzee",
      "hook-of-holland",
      "ijmuiden",
      "texel",
      "harlingen",
      "rottum",
      "delfzijl",
      "ijsselmeer",
      "marken",
    ],
    canonicalPhenomena: ["wind", "gust", "weather", "visibility"],
    phenomenaInlineKeys: MARITIME_BROADCAST_PHENOMENON_INLINE_KEYS,
    subtitle:
      "The weatherforecast for Netherlands coastal waters and adjacent lakes and estuaries.",
    warningHeader: "A warning to shipping:",
    otherAreasHeader: "Other districts:",
    otherAreasText: "no warning",
    noWarningsText: "There are no warnings",
    synopsisHeader: "Synopsis:",
    allDistrictsText: "All districts",
    littleChangeText: "little change",
    phenomenaSep: ", ",
    formatIssuedLine: issuedEN,
    formatPeriodHeader: periodHeaderEN_noDay,
    formatFooter: footerEN,
  },
  shipping_forecast_nl: {
    domain: MARITIME_FORECAST_DOMAINS.northSea,
    locale: "nl",
    canonicalAreaIds: ["dover", "thames", "humber", "dogger", "german-bight"],
    canonicalPhenomena: ["wind", "weather", "visibility"],
    phenomenaInlineKeys: MARITIME_BROADCAST_PHENOMENON_INLINE_KEYS,
    warningHeader: "Wind- of stormwaarschuwingen:",
    otherAreasHeader: "Overige districten:",
    otherAreasText: "geen waarschuwing",
    noWarningsText: "Er zijn geen waarschuwingen van kracht",
    synopsisHeader: "Weeroverzicht:",
    allDistrictsText: "Alle districten",
    littleChangeText: "weinig verandering",
    phenomenaSep: ", ",
    formatIssuedLine: issuedNL("UTC", true),
    formatPeriodHeader: periodHeaderSchip,
    formatFooter: footerNL("UTC", true),
  },
  navtex: {
    domain: MARITIME_FORECAST_DOMAINS.dutchContinentalShelf,
    locale: "en",
    canonicalAreaIds: ["thames", "humber", "german-bight", "dogger"],
    canonicalPhenomena: ["wind", "weather", "visibility", "significant_wave_height"],
    phenomenaInlineKeys: MARITIME_BROADCAST_PHENOMENON_INLINE_KEYS,
    subtitle: "Forecast Dutch EEZ",
    warningHeader: "Gale warnings:",
    otherAreasHeader: "Other areas:",
    otherAreasText: "no warning",
    noWarningsText: "There are no warnings",
    synopsisHeader: "Synopsis:",
    allDistrictsText: "All areas",
    littleChangeText: "little change",
    phenomenaSep: ", ",
    formatIssuedLine: issuedEN,
    formatPeriodHeader: periodHeaderNavtex,
    formatFooter: footerEN,
  },
};

// ─── Data processing ───────────────────────────────────────────────────────────

const buildAreaNameMap = (
  areas: MaritimeArea[],
  locale: "nl" | "en",
): Map<string, string> => new Map(areas.map((a) => [a.id, getAreaName(a, locale)]));

const buildWarningGroups = (
  warnings: MaritimeWarning[],
  canonicalAreaIds: string[],
  areaNameMap: Map<string, string>,
  locale: "nl" | "en",
): { warningGroups: WarningGroup[]; otherAreaNames: string[]; hasWarnings: boolean } => {
  // Primary warning per area: highest level wins
  const primaryByArea = new Map<string, MaritimeWarning>();
  for (const w of warnings) {
    if (!canonicalAreaIds.includes(w.area_id)) continue;
    const existing = primaryByArea.get(w.area_id);
    if (!existing || w.level > existing.level) {
      primaryByArea.set(w.area_id, w);
    }
  }

  const hasWarnings = primaryByArea.size > 0;

  if (!hasWarnings) {
    return { warningGroups: [], otherAreaNames: [], hasWarnings: false };
  }

  // Group areas by warning description text (in canonical order)
  const groupMap = new Map<string, string[]>();
  for (const areaId of canonicalAreaIds) {
    const w = primaryByArea.get(areaId);
    if (!w) continue;
    const desc = getWarningDescription(w, locale);
    const existing = groupMap.get(desc) ?? [];
    groupMap.set(desc, [...existing, areaNameMap.get(areaId) ?? areaId]);
  }

  const warningGroups: WarningGroup[] = Array.from(groupMap.entries()).map(
    ([description, areaNames]) => ({ description, areaNames }),
  );

  const otherAreaNames = canonicalAreaIds
    .filter((id) => !primaryByArea.has(id))
    .map((id) => areaNameMap.get(id) ?? id);

  return { warningGroups, otherAreaNames, hasWarnings };
};

const buildPeriods = (
  forecast: MaritimeForecast,
  config: ProductConfig,
  areaNameMap: Map<string, string>,
): BroadcastPeriod[] => {
  const prev0to12 = new Map<string, string>();

  return MARITIME_FORECAST_SHORT_RANGE_PERIOD_KEYS.map((periodKey) => {
    const periodDef = forecast.periods[periodKey];
    if (!periodDef || periodDef.type !== "time_range") return null;

    const header = config.formatPeriodHeader(periodDef.start, periodDef.end);

    // Build text per area in canonical order
    const areaTexts: Array<{ areaId: string; name: string; text: string }> = [];
    for (const areaId of config.canonicalAreaIds) {
      const areaData = forecast.areas.find((a) => a.area_id === areaId);
      const entries = areaData?.periods[periodKey] ?? [];
      const text = buildAreaPhenomenaText(entries, {
        locale: config.locale,
        phenomena: config.canonicalPhenomena,
        sep: config.phenomenaSep,
        phenomenaInlineKeys: config.phenomenaInlineKeys,
      });
      areaTexts.push({ areaId, name: areaNameMap.get(areaId) ?? areaId, text });
    }

    const isLittleChangePeriod = periodKey === MARITIME_FORECAST_PERIOD_KEYS.hours12_24;

    // Group areas with identical forecast texts
    const grouped: AreaLine[] = [];
    const processed = new Set<string>();

    for (const { areaId, name, text } of areaTexts) {
      if (processed.has(areaId)) continue;

      // Check if 12-24 is identical to 0-12 for this area (little change)
      const isLittleChange = isLittleChangePeriod && prev0to12.get(areaId) === text;

      if (isLittleChange) {
        grouped.push({
          names: [name],
          text: config.littleChangeText,
          isLittleChange: true,
        });
        processed.add(areaId);
        continue;
      }

      // Find all subsequent areas with the same text
      const sameAreas = areaTexts
        .filter((a) => !processed.has(a.areaId) && a.text === text)
        .map((a) => a.areaId);

      const allIdentical =
        sameAreas.length === config.canonicalAreaIds.length - processed.size &&
        processed.size === 0;

      if (allIdentical && sameAreas.length === config.canonicalAreaIds.length) {
        grouped.push({ names: [config.allDistrictsText], text });
        sameAreas.forEach((id) => processed.add(id));
      } else {
        const groupNames = sameAreas.map((id) => areaNameMap.get(id) ?? id);
        grouped.push({ names: groupNames, text });
        sameAreas.forEach((id) => processed.add(id));
      }
    }

    if (periodKey === MARITIME_FORECAST_PERIOD_KEYS.hours0_12) {
      areaTexts.forEach(({ areaId, text }) => prev0to12.set(areaId, text));
    }

    return { header, areas: grouped };
  }).filter((p): p is BroadcastPeriod => p !== null);
};

// ─── Widget component ──────────────────────────────────────────────────────────

const MaritimeBroadcast = ({ configuration }: MaritimeBroadcastProps) => {
  const broadcastType: MaritimeBroadcastType = isMaritimeBroadcastType(
    configuration.broadcast_type,
  )
    ? configuration.broadcast_type
    : "marifoon_nl";
  const config = PRODUCT_CONFIGS[broadcastType];

  const forecastQuery = useMaritimeForecastQuery(config.domain);
  const areasQuery = useMaritimeAreasQuery();
  const warningsQuery = useMaritimeWarningsQuery();

  const rendererProps = useMemo((): MaritimeBroadcastRendererProps | null => {
    const forecast = forecastQuery.data;
    const areas = areasQuery.data;
    const warnings = selectMaritimeWarnings(warningsQuery.data);
    if (!forecast || !areas || !warningsQuery.data) return null;

    const areaNameMap = buildAreaNameMap(areas, config.locale);

    const { warningGroups, otherAreaNames, hasWarnings } = buildWarningGroups(
      warnings,
      config.canonicalAreaIds,
      areaNameMap,
      config.locale,
    );

    const periods = buildPeriods(forecast, config, areaNameMap);

    const otherAreasHeader =
      hasWarnings && otherAreaNames.length > 0 ? config.otherAreasHeader : undefined;
    const otherAreasText =
      hasWarnings && otherAreaNames.length > 0 ? config.otherAreasText : undefined;

    return {
      headerSubtitle: config.subtitle,
      issuedLine: config.formatIssuedLine(forecast.published_at),
      warningHeader: config.warningHeader,
      warningGroups,
      otherAreasHeader,
      otherAreasText,
      noWarningsText: config.noWarningsText,
      hasWarnings,
      synopsisHeader: config.synopsisHeader,
      synopsis:
        config.locale === "nl"
          ? (forecast.synopsis.nl ?? forecast.synopsis.en ?? "")
          : (forecast.synopsis.en ?? forecast.synopsis.nl ?? ""),
      periods,
      footerLines: config.formatFooter(forecast.next_forecast_at),
    };
  }, [forecastQuery.data, areasQuery.data, warningsQuery.data, config]);

  if (forecastQuery.isLoading || areasQuery.isLoading || warningsQuery.isLoading) {
    return <div>Loading...</div>;
  }

  if (!rendererProps) return null;

  return <MaritimeBroadcastRenderer {...rendererProps} />;
};

export default MaritimeBroadcast;
